import { useState, useEffect, useRef } from 'react';
import {useNavigate} from "react-router";
import {useConfirm} from "material-ui-confirm";
import {useTranslation} from "react-i18next";
import useAlert from "../useAlert";
import * as queryString from 'query-string';

export function useGrid(repository, {onEdit, defaultFilter = {}, defaultSort = '', onDataUpdate = null}) {

	const envFilter = queryString.parse(window.location.search);

    const { t }  = useTranslation();
    const [list, setList] = useState([]);
	const [page, setPage] = useState(0);
    const [filter, setFilter] = useState(Object.assign(defaultFilter, envFilter))
    const [sort, setSort] = useState(defaultSort);
	const [isSearching, setIsSearching] = useState(false);
	const itemsOnPage = 50;
    const {showSuccess, showError} = useAlert();

	const navigate = useNavigate();
	const confirm = useConfirm();
    const isFirstRun = useRef(true);

	const items = () => {
		return mapItems(list.Items || []);
	};

	const mapItems = (input) => {
		return input.map(x => Object.assign(x, { id: x.Id }));
	};

	const totalCount = () => {
		return list.TotalCount || 0;
    }

    const dataSource = async (x) => {
        if (typeof repository == 'function') {
            return await repository(x);
        } else {
           return await repository.getAll(x);
        }
    }

    const getData = async () => {

        try {
            setIsSearching(true);
            const sourceFilter = Object.assign({}, filter, { sort: sort, page: page + 1, itemsOnPage: itemsOnPage });
            const response = await dataSource(sourceFilter);
            setList(response.data);
        } catch (err) {
            console.error(err);
        } finally {
            setIsSearching(false);
        }
    };

	const handlePageChange = (event) => {
	    setPage(event);
    }

	const handleDeleteItemClick = async (event) => {

        confirm({ description: t('Do you really want to delete this item ?') })
         .then(async () => {
             try {
                 const response = await repository.delete(event.Id);
                 getData();
                 showSuccess(t('Item has been deleted'));
            } catch (err) {

                 if (typeof (err[0])) {
                    showError(err[0].Message);
                 } else {
                    showError(t('Error deleting item. Please try again.'));
                 }
            }
         }).catch(() => {})
    }

    const handleEditItemClick = (event) => {
	    onEdit(event);
    }

    const onFilterChangedHandler = (event) => {

		const newFilter = Object.assign({}, filter, event.filter);
		if (JSON.stringify(newFilter) != JSON.stringify(filter)) {
            setFilter(newFilter);
        }
    }

    const onSortChangedHandler = (event) => {
        setSort(event.sort);
    }

    const handleUpdateGrid = () => {
	    setFilter(Object.assign({}, filter));
    }

    const setFilterValue = (key, value) => {

	    if (key == null && typeof(value) == 'object') {
	        setFilter(Object.assign({}, filter, value))
        } else {
	        setFilter(Object.assign({}, filter, { [key]: value }))
        }
    }

    useEffect(() => {
        if (isFirstRun.current) {
            return;
        }

        getData();
    }, [page]);

    useEffect(() => {
        if (onDataUpdate != null) {
            onDataUpdate(items());
        }
    }, [list]);

   useEffect(() => {
        if (isFirstRun.current) {
            return;
        }

        getData();
    }, [filter]);

    useEffect(() => {
        if (isFirstRun.current) {
            return;
        }

        getData();
    }, [sort]);

	useEffect(() => {

        if (isFirstRun.current) {
            if (JSON.stringify(filter) != JSON.stringify(defaultFilter)) {
                navigate({search: '?' + queryString.stringify(filter)});

            }
            return;
        }

        navigate({search: '?' + queryString.stringify(filter)});

    }, [filter]);

	useEffect(() => {

        if (isFirstRun.current) {
            isFirstRun.current = false;
            getData();
        }
	});

    return {
	      items: items,
          totalCount: totalCount,
          page: page,
          itemsOnPage: itemsOnPage,
          isSearching: isSearching,
          filter: filter,
          sort: sort,
          setFilterValue: setFilterValue,
          onFilterChangedHandler: onFilterChangedHandler,
          onSortChangedHandler: onSortChangedHandler,
          onDeleteHandler: handleDeleteItemClick,
          onEditHandler: handleEditItemClick,
          onPage: handlePageChange,
          updateGrid: handleUpdateGrid};
}